import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import {
  BreadcrumbEntry,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Header } from '@backstage/ui';

/** Metadata row; refTarget renders the value as a catalog entity link. */
export interface PageHeaderDescriptionItem {
  label: string;
  refTarget?: string;
  value: ReactNode;
}

export interface PageHeaderProps {
  actions?: ReactNode;
  /** Supplementary feature content below the native header. */
  afterTitle?: ReactNode;
  description?: string;
  descriptions?: ReadonlyArray<PageHeaderDescriptionItem>;
  /** Override the browser document title; defaults to title. */
  pageTitleOverride?: string;
  title: string;
  typeTag?: string;
}

/**
 * Adapt NS feature-page data to the native BUI Header. Upstream plugin pages
 * render their own headers; colours and typography are owned by the NS theme.
 * Breadcrumbs belong to the native plugin toolbar, not to a second title row.
 */
export const PageHeader = ({
  actions,
  afterTitle,
  description,
  descriptions = [],
  pageTitleOverride,
  title,
  typeTag,
}: PageHeaderProps) => {
  const { pathname } = useLocation();
  const { items } = useBreadcrumbEntries();
  const parent = items[0];
  // PageBlueprint already registers overview routes. Detail pages contribute
  // their title without repeating the overview entry or inventing home links.
  const isDetail =
    parent && parent.href.replace(/\/$/, '') !== pathname.replace(/\/$/, '');
  const header = (
    <>
      <Helmet
        defaultTitle="Developer Portal"
        title={pageTitleOverride ?? title}
        titleTemplate="%s | Developer Portal"
      />
      <Header
        customActions={actions}
        description={description}
        metadata={descriptions.map(({ label, refTarget, value }) => ({
          label,
          value: refTarget ? (
            <EntityRefLink entityRef={refTarget}>{value}</EntityRefLink>
          ) : (
            value
          ),
        }))}
        tags={typeTag ? [{ label: typeTag }] : undefined}
        title={title}
      />
      {afterTitle}
    </>
  );
  return isDetail ? (
    <BreadcrumbEntry entry={{ href: pathname, label: title }}>
      {header}
    </BreadcrumbEntry>
  ) : (
    header
  );
};
